'use client';
import React, { useEffect, useMemo, useRef, useState } from 'react';

const TikTokIcon = () => (
  <svg viewBox="0 0 256 256" className="w-6 h-6" fill="currentColor" aria-hidden="true">
    <path d="M168.9 24c8.3 22.1 26.4 39.5 49 46.6a86.8 86.8 0 0 0 9.9 2.3v39.4a124.2 124.2 0 0 1-49.4-10.1v63.8c0 36.7-29.8 66.5-66.5 66.5S45.4 202.8 45.4 166c0-36.7 29.8-66.5 66.5-66.5a66 66 0 0 1 16.8 2.2v41.5a26.2 26.2 0 0 0-16.8-6c-14.5 0-26.3 11.8-26.3 26.3s11.8 26.3 26.3 26.3c14.5 0 26.3-11.8 26.3-26.3V24h30.7z"/>
  </svg>
);
const FacebookIcon = () => (
  <svg viewBox="0 0 512 512" className="w-6 h-6" fill="currentColor" aria-hidden="true">
    <path d="M504 256C504 119 393 8 256 8S8 119 8 256c0 123.78 90.69 226.38 209.25 245V327.69h-63V256h63v-54.64c0-62.15 37-96.48 93.67-96.48 27.14 0 55.52 4.84 55.52 4.84v61h-31.28c-30.8 0-40.41 19.12-40.41 38.73V256h68.78l-11 71.69h-57.78V501C413.31 482.38 504 379.78 504 256z"/>
  </svg>
);
const InstagramIcon = () => (
  <svg
    viewBox="0 0 24 24"
    className="w-7 h-7"
    aria-hidden="true"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {/* rounded square */}
    <rect x="3" y="3" width="18" height="18" rx="5" />
    {/* lens */}
    <circle cx="12" cy="12" r="4.2" />
    {/* flash dot */}
    <circle cx="17.5" cy="6.5" r="1.2" fill="currentColor" stroke="none" />
  </svg>
);


/* GA helper: ignore if embedded */
function ga(event: string, params?: Record<string, unknown>) {
  try {
    if (typeof window === 'undefined') return;
    if (window.top !== window.self) return; // parent-only iframes: keep this
    const w: any = window;
    if (Array.isArray(w.dataLayer)) {
      w.dataLayer.push({ event, ...(params || {}) });
      return;
    }
    // Fallback if gtag is still present in older deploys
    if (typeof w.gtag === 'function') {
      w.gtag('event', event, params || {});
    }
  } catch {}
}


/* Simple domain→network mapping for WP links */
function guessNetwork(href: string): 'tiktok'|'facebook'|'instagram'|'other' {
  try {
    const h = new URL(href).hostname;
    if (h.includes('tiktok')) return 'tiktok';
    if (h.includes('facebook')) return 'facebook';
    if (h.includes('instagram')) return 'instagram';
    return 'other';
  } catch { return 'other'; }
}

type Social = { name: string; href: string };

const Footer: React.FC = () => {
  // defaults
  const [phonePretty, setPhonePretty] = useState('(888) 714-PROS');
  const [phoneHref, setPhoneHref] = useState('tel:1-888-714-7767');
  const [addressText, setAddressText] = useState('309 West Nakoma St, San Antonio, TX, 78216');
  const [mapsHref, setMapsHref] = useState(
    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent('309 West Nakoma St, San Antonio, TX, 78216')}`
  );
  const [social, setSocial] = useState<Social[]>([
    { name: 'TikTok', href: 'https://www.tiktok.com/@water.edge.techno' },
    { name: 'Facebook', href: 'https://www.facebook.com/wateredgetech/' },
    { name: 'Instagram', href: 'https://www.instagram.com/wateredgetech/' },
  ]);

  // WP overrides
  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();
    (async () => {
      try {
        const r = await fetch('https://wateredgetech.com/wp-json/wet/v1/marketing', {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
          signal: ctrl.signal,
        });
        if (!r.ok) return;
        const data = await r.json();
        const f = data?.footer || {};
        const v = (s?: string) => (typeof s === 'string' && s.trim() ? s.trim() : undefined);

        if (cancelled) return;

        const pPretty = v(f.phone_pretty);
        const pHref = v(f.phone_href);
        const addr = v(f.address);
        const map = v(f.maps_href);
        const socials = Array.isArray(f.social) ? f.social : null;

        if (pPretty) setPhonePretty(pPretty);
        if (pHref) setPhoneHref(pHref);
        if (addr) setAddressText(addr);
        if (map) setMapsHref(map);
        if (socials) {
          const cleaned: Social[] = socials
            .map((s: any) => ({ name: String(s?.name || ''), href: String(s?.href || '') }))
            .filter((s: Social) => s.href);
          if (cleaned.length) setSocial(cleaned);
        }
      } catch {}
    })();
    return () => { cancelled = true; ctrl.abort(); };
  }, []);

  // impression once
  const ref = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let fired = false;
    const obs = new IntersectionObserver((ents) => {
      if (fired) return;
      if (ents.some(e => e.isIntersecting)) {
        fired = true;
        ga('section_view', { section: 'footer' });
        obs.disconnect();
      }
    }, { threshold: 0.4 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const onPhoneClick = () => ga('click_phone', { location: 'footer', phone: phonePretty });
  const onAddressClick = () => ga('click_address', { location: 'footer', address: addressText });

  const socialWithIcon = useMemo(() => {
    return social.map(s => {
      const n = guessNetwork(s.href);
      return { ...s, n };
    });
  }, [social]);

  return (
    <footer ref={ref} className="bg-slate-900/50 text-white">
      <div className="container mx-auto px-6 py-10 text-center">
        <div className="mb-4 flex flex-col items-center justify-center gap-4 sm:flex-row sm:gap-8">
          <a
            href={phoneHref}
            onClick={onPhoneClick}
            className="text-gray-300 hover:text-white transition-colors duration-200"
          >
            {phonePretty}
          </a>
          <span className="hidden sm:inline-block text-gray-500">|</span>
          <a
            href={mapsHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onAddressClick}
            className="text-gray-300 hover:text-white transition-colors duration-200"
          >
            {addressText}
          </a>
        </div>

        {/* Social icons row */}
        <div className="mb-4 flex items-center justify-center gap-6">
          {socialWithIcon.map(({ name, href, n }) => {
            const onClick = () => ga('click_social', { location: 'footer', network: n, url: href });
            const Icon =
              n === 'tiktok' ? TikTokIcon :
              n === 'facebook' ? FacebookIcon :
              n === 'instagram' ? InstagramIcon :
              null;
            return (
              <a
                key={`${name}-${href}`}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={name}
                onClick={onClick}
                className="text-gray-300 hover:text-white transition-colors duration-200"
              >
                {Icon ? <Icon /> : <span className="text-sm">{name}</span>}
              </a>
            );
          })}
        </div>

        <p className="text-gray-400 text-sm">&copy; {new Date().getFullYear()} Get clean. Get WET.</p>
      </div>
    </footer>
  );
};

export default Footer;
